import { Controller } from 'react-hook-form'
import { FormControl, FormErrorMessage, FormLabel } from '@chakra-ui/react'
import { Select } from 'chakra-react-select'

import { useEventFormOptions } from '@hooks/useEventFormOptions'

import {
    MatchEventEditorEventType,
    MatchEventEditorFixtureExtraInfo,
    MatchEventPeriodNumber,
    TMatchEventPeriodNumber,
} from '@common/types'

import { useMatchEditorFormContext } from '@contexts/MatchEditorFormContext'

import { findOption } from '../utils'

const MATCH_EVENT_PERIOD_MAPPING = new Map<number, TMatchEventPeriodNumber>([
    [MatchEventEditorFixtureExtraInfo.start_1st_half, MatchEventPeriodNumber.first_half],
    [MatchEventEditorFixtureExtraInfo.half_time, MatchEventPeriodNumber.first_half],
    [MatchEventEditorFixtureExtraInfo.start_2nd_half, MatchEventPeriodNumber.second_half],
    [MatchEventEditorFixtureExtraInfo.finish_after_regular_time, MatchEventPeriodNumber.second_half],
    [MatchEventEditorFixtureExtraInfo.awaiting_extra_time, MatchEventPeriodNumber.first_half_extra_time],
    [MatchEventEditorFixtureExtraInfo.start_1st_half_extra_time, MatchEventPeriodNumber.first_half_extra_time],
    [MatchEventEditorFixtureExtraInfo.half_time_extra_time, MatchEventPeriodNumber.first_half_extra_time],
    [MatchEventEditorFixtureExtraInfo.start_2nd_half_extra_time, MatchEventPeriodNumber.second_half_extra_time],
    [MatchEventEditorFixtureExtraInfo.finish_after_extra_time, MatchEventPeriodNumber.second_half_extra_time],
    [MatchEventEditorFixtureExtraInfo.awaiting_penalty_shootout, MatchEventPeriodNumber.penalties],
    [MatchEventEditorFixtureExtraInfo.start_penalty_shootout, MatchEventPeriodNumber.penalties],
    [MatchEventEditorFixtureExtraInfo.finish_after_penalty_shootout, MatchEventPeriodNumber.penalties],
])

export function MatchEventExtraInfoSelect() {
    const { zodForm, isFormDisabled } = useMatchEditorFormContext()
    const { matchEvents, periods } = useEventFormOptions()

    const selectedEventType = zodForm.watch('type.value')

    if (Number(selectedEventType) !== MatchEventEditorEventType.fixture_status) {
        return null
    }

    const onMatchEventChanges = (newValue: number) => {
        const option = MATCH_EVENT_PERIOD_MAPPING.get(newValue)
        if (option) {
            zodForm.setValue('period_number', findOption(String(option), periods), {
                shouldDirty: true,
            })
        } else {
            zodForm.setValue('period_number', findOption(String(MatchEventPeriodNumber.first_half), periods), {
                shouldDirty: true,
            })
        }
    }

    return (
        <Controller
            control={zodForm.control}
            name="extra_info"
            render={({ field: { onChange, onBlur, value, name, ref }, fieldState: { error } }) => (
                <FormControl isInvalid={!!error} isRequired mb={8} isDisabled={isFormDisabled}>
                    <FormLabel htmlFor="type">Select Period Event</FormLabel>
                    <Select
                        id="type"
                        isSearchable
                        name={name}
                        ref={ref}
                        onChange={v => {
                            const newValue = Number(v.value)
                            onMatchEventChanges(newValue)

                            onChange(newValue)
                        }}
                        onBlur={onBlur}
                        value={findOption(String(value), matchEvents)}
                        placeholder="Select Period Event"
                        options={matchEvents}
                    />

                    <FormErrorMessage>{zodForm.formState.errors.type?.message}</FormErrorMessage>
                </FormControl>
            )}
        />
    )
}
